import { Button } from "@arco-design/web-react";
import { useState } from "react";
import { formatMoney } from "../service/quoteBudgetEditorViewService";
import { buildManualBusRegion } from "../service/quoteBudgetManualTableService";
import type { ManualEntryCategory, ManualEntryContext, ManualEntryDraft, ManualEntryItem, ManualEntryParseResult } from "../types/manualEntry";
import "./quoteBudgetManualPreview.css";

/** 只呈现解析结果，确认及数据库写入由宿主负责。 */
export type QuoteBudgetManualPreviewProps = {
  category: ManualEntryCategory;
  preview: ManualEntryParseResult | null;
  locate: (start: number, end: number) => void;
  editing?: boolean;
  itineraryDays?: ManualEntryContext["itineraryDays"];
  /** 左侧光标所在原文起点，用于右侧滚动并高亮对应预览项。 */
  activeSourceStart?: number | null;
};

type PreviewRow = { draft: ManualEntryDraft; item: ManualEntryItem };
type PreviewGroup = { id: string; label: string; rows: PreviewRow[] };
type Locate = QuoteBudgetManualPreviewProps["locate"];

const TOTAL_LABELS: Record<ManualEntryCategory, string> = {
  stay: "本区域合计", point: "本点合计", crew: "司导合计", misc: "杂费合计",
  air: "待加入合计", ship: "待加入合计", bus: "待加入合计", meal: "本次合计",
};
const EMPTY_LABELS: Record<ManualEntryCategory, string> = {
  stay: "输入住宿费用后实时预览", point: "输入本点费用后实时预览",
  crew: "点击常用项目或直接输入司导费用", misc: "点击常用项目或直接输入杂费",
  air: "填写城市路线和报价后实时预览", ship: "填写城市路线和报价后实时预览", bus: "输入用车内容后，逐项核对车型、辆数和价格。",
  meal: "输入餐费后实时预览",
};

/** 复用现有精确金额格式化，只补齐当前界面要求的两位小数。 */
function money(value: string): string {
  return formatMoney(value).replace(/¥([\d,]+)(?:\.(\d+))?$/, (_match, integer: string, fraction: string | undefined) =>
    `¥${integer}.${(fraction ?? "").padEnd(2, "0")}`);
}

/** 仅累加解析器已经计算的金额，以整数分保留大额分位，不重新计算报价。 */
function groupMoney(group: PreviewGroup): string {
  const cents = group.rows.reduce((total, { draft }) => {
    const [integer, fraction = ""] = draft.amount.split(".");
    return total + BigInt(integer) * 100n + BigInt(fraction.padEnd(2, "0"));
  }, 0n);
  return money(`${cents / 100n}.${String(cents % 100n).padStart(2, "0")}`);
}

/** 分组只使用解析器提供的稳定业务身份，错误片段在外层单独保留。 */
function previewGroups(items: ManualEntryItem[]): PreviewGroup[] {
  const groups = new Map<string, PreviewGroup>();
  for (const item of items) for (const draft of item.drafts) {
    const { groupId, groupLabel } = draft.entrySnapshot;
    const group = groups.get(groupId) ?? { id: groupId, label: groupLabel, rows: [] };
    group.rows.push({ draft, item });
    groups.set(groupId, group);
  }
  return [...groups.values()];
}

/** 明确人数与周期分别显示，零人数不能反推或丢弃原周期。 */
function peopleText(row: ManualEntryDraft): string {
  return row.explicitPeopleCount === null
    ? String(BigInt(row.quantity) / BigInt(row.entrySnapshot.billingCount))
    : String(row.explicitPeopleCount);
}

/** 列表预览不再展示随人数/固定人数；仅保留场次与整项等非人数模式说明。 */
function modeText(row: ManualEntryDraft): string | null {
  switch (row.entrySnapshot.quantityMode) {
    case "linked_people":
    case "fixed_people":
      return null;
    case "quantity": return row.unit === "场" ? "按场次 · 不乘人数" : row.unit === "桌" ? "按桌 · 不乘人数" : "固定数量 · 不乘人数";
    case "total": return "整项金额";
  }
}

/** 公式直接展开解析因素；总额行不再重复单价。 */
function formulaText(row: ManualEntryDraft): string {
  const price = row.unitPriceText === null ? "待补价" : `${row.unitPriceText}元`;
  const { quantityMode, billingCount } = row.entrySnapshot;
  if (quantityMode === "total") return "整项金额";
  if (quantityMode === "quantity") return `${row.quantity}${row.unit} × ${price}`;
  const unit = row.unit === "人晚" ? "晚" : row.unit === "人天" ? "天" : row.unit === "人餐" ? "餐" : "次";
  return `${peopleText(row)}人 × ${billingCount}${unit} × ${price}`;
}

/** 定位只使用解析器保存的原文区间，不重新搜索或改写输入。 */
function SourceLink({ item, locate }: { item: ManualEntryItem; locate: Locate }) {
  return <Button type="text" size="mini" style={{ padding: 0, fontSize: 12 }} aria-label={`定位原文 ${item.source.line}`} onClick={() => locate(item.source.start, item.source.end)}>第 {item.source.line} 行 ↗</Button>;
}

/** 问题和提醒完整展示；缺价沿用可加入规则，不把缺价画成免费。 */
function ItemMessages({ item }: { item: ManualEntryItem }) {
  return <>{item.issues.map((issue, index) => <p key={`${issue.code}:${index}`} className={item.status === "missing_price" ? "qb-cost-preview__warning" : "qb-cost-preview__error"}>{issue.message}</p>)}{item.warnings.map((warning, index) => <p key={index} className="qb-cost-preview__warning">{warning}</p>)}</>;
}

/** 不可识别的原文仍可查看并定位，不能在分组时被过滤。 */
function ErrorItem({ item, locate }: { item: ManualEntryItem; locate: Locate }) {
  return <div className="qb-cost-preview__invalid"><div className="qb-cost-preview__meta"><strong>{item.status === "missing_factor" ? "待补充" : "待修改"}</strong><SourceLink item={item} locate={locate} /></div><p className="qb-cost-preview__source">{item.source.text || "空片段"}</p><ItemMessages item={item} /></div>;
}

/** 住宿、门票、司导及杂费使用原型的两行公式卡片。 */
function PreviewCards({ category, items, locate, activeSourceStart }: { category: ManualEntryCategory; items: ManualEntryItem[]; locate: Locate; activeSourceStart?: number | null }) {
  return <>{items.map((item, index) => <article className={`qb-cost-preview__card${activeSourceStart === item.source.start ? " qb-cost-preview__card--active" : ""}`} data-preview-source={item.source.start} key={`${item.source.start}:${index}`}>
    {item.drafts.length ? <>{item.drafts.map((row, rowIndex) => <div className="qb-cost-preview__entry" key={rowIndex}>
      <div className="qb-cost-preview__meta"><strong>{category === "stay" ? `${row.role} · ${row.name}` : row.name}</strong>{(() => {
        const mode = modeText(row);
        const label = category === "point" && row.role ? `${row.role}${mode ? ` · ${mode}` : ""}` : mode;
        return label ? <span className="qb-cost-preview__mode">{label}</span> : null;
      })()}<SourceLink item={item} locate={locate} /></div>
      <div className="qb-cost-preview__formula">{formulaText(row)} = <b>{row.unitPriceText === null ? "待补价" : money(row.amount)}</b></div>
    </div>)}<ItemMessages item={item} /></> : <ErrorItem item={item} locate={locate} />}
  </article>)}</>;
}

/** 同一来源的提示只在所属组末尾展示一次，不因车型重复堆叠。 */
function GroupMessages({ group, columns, locate }: { group: PreviewGroup; columns: number; locate: Locate }) {
  const items = [...new Set(group.rows.map(row => row.item))].filter(item => item.issues.length || item.warnings.length);
  return <>{items.map((item, index) => <tr className="qb-cost-preview__notes" key={index}><td colSpan={columns}><SourceLink item={item} locate={locate} /><ItemMessages item={item} /></td></tr>)}</>;
}

/** 飞机与船以城市路线组织四列预览，价格、人数与路线小计分开核对。 */
function CityTransportPreview({ groups, errors, locate, activeSourceStart }: { groups: PreviewGroup[]; errors: ManualEntryItem[]; locate: Locate; activeSourceStart?: number | null }) {
  return <table className="qb-cost-preview__table qb-cost-preview__table--air">
    <colgroup>{[29, 18, 24, 29].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}</colgroup>
    <thead><tr><th>角色 / 费用</th><th>人数</th><th>单价</th><th>金额</th></tr></thead>
    {groups.map(group => <tbody key={group.id}><tr className="qb-cost-preview__group-title"><td colSpan={4}>{group.label}</td></tr>
      {group.rows.map(({ draft, item }, index) => <tr className={activeSourceStart === item.source.start ? "qb-cost-preview__row--active" : undefined} data-preview-source={item.source.start} key={index}><td><Button type="text" size="mini" style={{ padding: 0 }} onClick={() => locate(item.source.start, item.source.end)}>{draft.role} · {draft.resourceEntryType || (draft.category === "ship" ? "船票" : "机票")}</Button></td><td>{peopleText(draft)}</td><td>{draft.unitPriceText === null ? <span className="qb-cost-preview__warning">待补价</span> : money(draft.unitPriceText)}</td><td>{draft.unitPriceText === null ? <span className="qb-cost-preview__warning">待补价</span> : money(draft.amount)}</td></tr>)}
      <GroupMessages group={group} columns={4} locate={locate} />
      <tr className="qb-cost-preview__subtotal"><td colSpan={3}>{group.rows.some(row => row.item.status !== "valid") ? "路线已识别小计" : "路线小计"}</td><td>{groupMoney(group)}</td></tr>
    </tbody>)}
    {errors.length ? <tbody>{errors.map((item, index) => <tr key={index} data-preview-source={item.source.start} className={activeSourceStart === item.source.start ? "qb-cost-preview__row--active" : undefined}><td colSpan={4}><ErrorItem item={item} locate={locate} /></td></tr>)}</tbody> : null}
  </table>;
}

/** 大巴范围由快照里的真实日期和服务组成，不能把分散日期伪装为连续包车。 */
function busLabel(group: PreviewGroup): string {
  const context = group.rows[0].draft.entrySnapshot.context;
  const days = context?.dayNumbers ?? [];
  if (!days.length) return group.label;
  const continuous = days.length > 1 && days.every((day, index) => index === 0 || day === days[index - 1] + 1);
  const scope = continuous ? `${days[0]}-${days.at(-1)}` : days.join("、");
  const service = context?.serviceType === "pickup" ? "单接" : context?.serviceType === "dropoff" ? "单送" : "当地用车";
  return `第${scope}天 · ${service}`;
}

/** 区域与主表共用原型规则，不把行程中的占位文字显示为地点。 */
function busRegion(group: PreviewGroup, itineraryDays: ManualEntryContext["itineraryDays"]): string {
  return buildManualBusRegion(group.rows[0].draft.entrySnapshot.context?.dayNumbers ?? [], itineraryDays);
}

/** 展开收起只控制已识别车型行，问题、组小计和完整总计始终保留。 */
function BusPreview({ groups, errors, locate, itineraryDays, activeSourceStart }: { groups: PreviewGroup[]; errors: ManualEntryItem[]; locate: Locate; itineraryDays: ManualEntryContext["itineraryDays"]; activeSourceStart?: number | null }) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const scopeCell = (group: PreviewGroup) => <td className="qb-cost-preview__scope" rowSpan={collapsed[group.id] ? 1 : group.rows.length}>
    <Button type="text" size="mini" style={{ padding: 0, height: "auto", whiteSpace: "normal", textAlign: "left" }} aria-label={`${collapsed[group.id] ? "展开" : "收起"}${busLabel(group)}`} aria-expanded={!collapsed[group.id]} onClick={() => setCollapsed(previous => ({ ...previous, [group.id]: !previous[group.id] }))}>{collapsed[group.id] ? "▸ " : "▾ "}{busLabel(group)}</Button>
    {busRegion(group, itineraryDays) ? <p className="qb-cost-preview__region">{busRegion(group, itineraryDays)}</p> : null}
    <SourceLink item={group.rows[0].item} locate={locate} />
  </td>;
  return <table className="qb-cost-preview__table qb-cost-preview__table--bus">
    <colgroup>{[27, 16, 10, 22, 25].map(width => <col key={width} style={{ width: `${width}%` }} />)}</colgroup>
    <thead><tr><th>范围 / 服务</th><th>车型</th><th>辆数</th><th>单车价</th><th>金额</th></tr></thead>
    {groups.map(group => <tbody key={group.id}>
      {collapsed[group.id] ? <tr className={group.rows.some(row => activeSourceStart === row.item.source.start) ? "qb-cost-preview__row--active" : undefined} data-preview-source={group.rows[0]?.item.source.start}>{scopeCell(group)}<td colSpan={4} className="qb-cost-preview__collapsed">{group.rows.length} 条车型已收起</td></tr> : group.rows.map(({ draft, item }, index) => <tr className={activeSourceStart === item.source.start ? "qb-cost-preview__row--active" : undefined} data-preview-source={item.source.start} key={index}>{index === 0 ? scopeCell(group) : null}<td className="qb-cost-preview__model"><Button type="text" size="mini" style={{ padding: 0 }} onClick={() => locate(item.source.start, item.source.end)}>{draft.entrySnapshot.context?.vehicleSpec ?? draft.name}</Button></td><td>{draft.quantity}</td><td>{draft.unitPriceText === null ? <span className="qb-cost-preview__warning">待补价</span> : money(draft.unitPriceText)}</td><td>{draft.unitPriceText === null ? <span className="qb-cost-preview__warning">待补价</span> : money(draft.amount)}</td></tr>)}
      <GroupMessages group={group} columns={5} locate={locate} />
      <tr className="qb-cost-preview__subtotal"><td colSpan={4}>{group.rows.some(row => row.item.status !== "valid") ? "已识别小计" : "本笔小计"}</td><td>{groupMoney(group)}</td></tr>
    </tbody>)}
    {errors.length ? <tbody>{errors.map((item, index) => <tr key={index} data-preview-source={item.source.start} className={activeSourceStart === item.source.start ? "qb-cost-preview__row--active" : undefined}><td colSpan={5}><ErrorItem item={item} locate={locate} /></td></tr>)}</tbody> : null}
  </table>;
}

/** 非餐预览拥有标题、可滚动结果与固定合计，所有费用和原文状态来自同一次解析。 */
export function QuoteBudgetManualPreview({ category, preview, locate, editing = false, itineraryDays, activeSourceStart = null }: QuoteBudgetManualPreviewProps) {
  const items = preview?.items ?? [];
  const groups = previewGroups(items);
  const errors = items.filter(item => !item.drafts.length);
  const rows = items.flatMap(item => item.drafts);
  const incomplete = items.some(item => item.status !== "valid");
  const totalLabel = editing && (category === "air" || category === "ship" || category === "bus") ? "修改后合计" : TOTAL_LABELS[category];
  return <section className={`qb-cost-preview qb-cost-preview--${category}`} aria-label="实时预览">
    <header className="qb-cost-preview__heading"><strong>实时预览</strong><span>{category === "bus" ? `${groups.length} 笔用车 · ${rows.length} 条车型` : `${rows.length} 项费用`}</span></header>
    <div className="qb-cost-preview__scroll">{!items.length ? <p className="qb-cost-preview__empty">{EMPTY_LABELS[category]}</p> : category === "bus" ? <BusPreview groups={groups} errors={errors} locate={locate} itineraryDays={itineraryDays} activeSourceStart={activeSourceStart} /> : category === "air" || category === "ship" ? <CityTransportPreview groups={groups} errors={errors} locate={locate} activeSourceStart={activeSourceStart} /> : <PreviewCards category={category} items={items} locate={locate} activeSourceStart={activeSourceStart} />}</div>
    <footer className="qb-cost-preview__total"><span>{totalLabel}{incomplete ? "（成本未完整）" : ""}</span><strong>{money(preview?.knownTotalAmount ?? "0.00")}</strong></footer>
  </section>;
}
